import moment from 'moment';
import Utils from './Utils';
import { compute_programmation_stop_at } from './programmationCreateOptionsPrefs';

/**
 * Weekday indices (0=Sun … 6=Sat) that appear at least once in [start_at, stop_at].
 * @param {moment.Moment|Date|string} start_at
 * @param {moment.Moment|Date|string} stop_at
 * @returns {number[]}
 */
export function allowed_weekdays_in_range(start_at, stop_at) {
    if (!start_at || !stop_at)
        return [];
    const start = moment(start_at).startOf('day');
    const end = moment(stop_at).startOf('day');
    if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day'))
        return [];

    const allowed = [];
    const seen = new Set();
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, 'day')) {
        const day = cursor.day();
        if (!seen.has(day)) {
            seen.add(day);
            allowed.push(day);
        }
        cursor.add(1, 'day');
    }
    return allowed;
}

/**
 * Constrain slot weekdays only for period_type=day with n ≤ 7
 * (each weekday appears at most once in the window).
 * @param {string|null|undefined} period_type
 * @param {number|string|null|undefined} period_nb
 * @returns {boolean}
 */
export function should_constrain_programmation_days(period_type, period_nb) {
    if (period_type !== Utils.PERIOD_DAY)
        return false;
    const n = Number(period_nb);
    const count = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
    return count <= 7;
}

/**
 * i18n key for the date preposition before start_at.
 * "on the" when a single calendar day; otherwise "from the".
 * @param {string|null|undefined} period_type
 * @param {number|string|null|undefined} period_nb
 * @returns {'programmations.on_the'|'programmations.from_the'}
 */
export function programmation_period_date_preposition_key(period_type, period_nb) {
    const n = Number(period_nb);
    const count = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
    if (period_type === Utils.PERIOD_DAY && count === 1)
        return 'programmations.on_the';
    return 'programmations.from_the';
}

/**
 * @param {string|null|undefined} period_type
 * @param {number|string|null|undefined} period_nb
 * @returns {boolean}
 */
export function is_single_calendar_day_period(period_type, period_nb) {
    return programmation_period_date_preposition_key(period_type, period_nb)
        === 'programmations.on_the';
}

/**
 * True when each weekday appears at most once in the period window
 * (1 day, ≤7 days, 1 calendar/cinema week).
 *
 * @param {moment.Moment|Date|string|null|undefined} start_at
 * @param {number|string|null|undefined} period_nb
 * @param {string|null|undefined} period_type
 * @returns {boolean}
 */
export function period_has_unique_weekdays(start_at, period_nb, period_type) {
    if (!start_at)
        return false;
    const stop_at = compute_programmation_stop_at(start_at, period_nb, period_type);
    const start = moment(start_at).startOf('day');
    const end = moment(stop_at).startOf('day');
    if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day'))
        return false;
    const span_days = end.diff(start, 'days') + 1;
    return span_days > 0 && span_days <= 7;
}

/**
 * First calendar date in [start_at, stop_at] whose weekday matches `weekday`
 * (0=Sun … 6=Sat).
 *
 * @param {moment.Moment|Date|string} start_at
 * @param {moment.Moment|Date|string} stop_at
 * @param {number} weekday
 * @returns {moment.Moment|null}
 */
export function date_for_weekday_in_range(start_at, stop_at, weekday) {
    if (weekday == null || weekday === '')
        return null;
    const start = moment(start_at).startOf('day');
    const end = moment(stop_at).startOf('day');
    if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day'))
        return null;
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, 'day')) {
        if (cursor.day() === Number(weekday))
            return cursor.clone();
        cursor.add(1, 'day');
    }
    return null;
}

/**
 * Slot header label for a one-day period, e.g. "le mardi 15.09.2026".
 *
 * @param {function} t
 * @param {string} lang
 * @param {moment.Moment|Date|string|null|undefined} start_at
 * @returns {string|null}
 */
export function format_single_day_programmation_label(t, lang, start_at) {
    if (!start_at)
        return null;
    const formatted = Utils.formatLocalizedDateTime(start_at, lang, 'dddd L');
    if (!formatted || formatted === '—')
        return null;
    const label = `${t('programmations.on_the')} ${formatted}`;
    return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * When each selected weekday occurs once in the period, list concrete dates
 * instead of « Tous les mardis » (1 week / 1 cinema week / short day windows).
 *
 * @param {function} t
 * @param {string} lang
 * @param {number[]|null|undefined} days
 * @param {{ start_at?: *, period_type?: string, period_nb?: number|string }|null|undefined} period
 * @returns {string|null}
 */
export function format_unique_weekday_dates_label(t, lang, days, period) {
    if (!period || !Array.isArray(days) || days.length === 0)
        return null;
    if (!period_has_unique_weekdays(period.start_at, period.period_nb, period.period_type))
        return null;
    const stop_at = compute_programmation_stop_at(
        period.start_at,
        period.period_nb,
        period.period_type
    );
    const labels = days
        .map((weekday) => {
            const date = date_for_weekday_in_range(period.start_at, stop_at, weekday);
            return date ? format_single_day_programmation_label(t, lang, date) : null;
        })
        .filter(Boolean);
    if (!labels.length)
        return null;
    if (labels.length === 1)
        return labels[0];
    // Only the sentence start is capitalized (« Le mardi … et le jeudi … »).
    const [first, ...rest] = labels;
    return Utils.joinWithComma([
        first,
        ...rest.map((label) => label.charAt(0).toLowerCase() + label.slice(1)),
    ], t);
}

/**
 * @param {object} params
 * @param {number[]|null|undefined} params.days
 * @param {moment.Moment|Date|string} params.start_at
 * @param {number|string} params.period_nb
 * @param {string} params.period_type
 * @returns {{ days: number[], allowed: number[]|null, locked: boolean, constrained: boolean }}
 */
export function constrain_slot_days_for_period({
    days,
    start_at,
    period_nb,
    period_type,
}) {
    if (!should_constrain_programmation_days(period_type, period_nb)) {
        return {
            days: Array.isArray(days) ? [...days] : [],
            allowed: null,
            locked: false,
            constrained: false,
        };
    }

    const stop_at = compute_programmation_stop_at(start_at, period_nb, period_type);
    const allowed = allowed_weekdays_in_range(start_at, stop_at);
    const n = Number(period_nb);
    const count = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
    const locked = count === 1 || allowed.length === 1;

    if (locked) {
        const only = allowed[0];
        return {
            days: only == null ? [] : [only],
            allowed,
            locked: true,
            constrained: true,
        };
    }

    const current = Array.isArray(days) ? days : [];
    const filtered = current.filter((d) => allowed.includes(d));
    return {
        days: filtered,
        allowed,
        locked: false,
        constrained: true,
    };
}

/**
 * @param {object[]} programmations
 * @param {object} period
 * @param {moment.Moment|Date|string} period.start_at
 * @param {number|string} period.period_nb
 * @param {string} period.period_type
 * @returns {{ programmations: object[], changed: boolean }}
 */
export function sync_programmations_days_to_period(programmations, period) {
    if (!Array.isArray(programmations) || programmations.length === 0) {
        return { programmations: programmations || [], changed: false };
    }

    let changed = false;
    const next = programmations.map((slot) => {
        const { days } = constrain_slot_days_for_period({
            days: slot?.days,
            start_at: period.start_at,
            period_nb: period.period_nb,
            period_type: period.period_type,
        });
        if (same_day_set(slot?.days, days))
            return slot;
        changed = true;
        return { ...slot, days };
    });

    return { programmations: next, changed };
}

function same_day_set(a, b) {
    const left = Array.isArray(a) ? [...a].map(Number).sort((x, y) => x - y) : [];
    const right = Array.isArray(b) ? [...b].map(Number).sort((x, y) => x - y) : [];
    if (left.length !== right.length)
        return false;
    return left.every((v, i) => v === right[i]);
}
